import React, { useState, useEffect, useRef } from 'react';
import AmountInput from '../shared/AmountInput';
import { accountsAPI, subscriptionsAPI } from '../../services/api';
import { listCategories } from '../../services/categories';

const ACCOUNT_COLORS = ['#7C3AED', '#2563EB', '#059669', '#EA580C', '#DB2777', '#0891B2'];

const fmt = (amount) =>
  Math.abs(amount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const ordinal = (n) => (n === 1 ? '1er' : `${n}e`);

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

/* ── Shared modal shell ── */
const Modal = ({ title, onClose, onSubmit, children, width = 520 }) => {
  const submitRef = useRef(onSubmit);
  useEffect(() => { submitRef.current = onSubmit; });
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'Enter' && submitRef.current) {
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag !== 'textarea' && tag !== 'select' && tag !== 'button') {
          e.preventDefault();
          submitRef.current();
        }
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-panel" style={{ maxWidth: width }}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
};

/* ── Subscription form modal (create + edit) ── */
const SubscriptionModal = ({ title, sub, accountId, categories, onClose, onSave, onDelete }) => {
  const [name, setName] = useState(sub?.name || '');
  const [type, setType] = useState(sub?.type || 'expense');
  const [categoryId, setCategoryId] = useState(sub?.categoryId || null);
  const [amount, setAmount] = useState(sub ? String(sub.amount) : '');
  const [dayOfMonth, setDayOfMonth] = useState(sub?.dayOfMonth || new Date().getDate());
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const filteredCats = categories.filter(c => c.type === type);

  const handleSave = async () => {
    if (saving) return;
    if (!name.trim()) { setError('Le nom de l\'abonnement est requis.'); return; }
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) { setError('Montant invalide.'); return; }
    if (!categoryId) { setError('Veuillez choisir une catégorie.'); return; }
    setSaving(true);
    setError('');
    try {
      const data = { name: name.trim(), type, categoryId, amount: Number(amount), dayOfMonth, accountId };
      if (sub) {
        await onSave(sub._id, data);
      } else {
        await onSave(data);
      }
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur lors de l\'enregistrement.');
      setSaving(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose} onSubmit={handleSave} width={520}>
      <div className="modal-fields">
        {/* Name */}
        <div className="field-group">
          <label className="field-label">Nom de l'abonnement</label>
          <input
            className="field-input"
            type="text"
            placeholder="ex : Netflix, Spotify, Salle de sport..."
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={100}
            autoFocus
          />
        </div>

        {/* Type toggle */}
        <div className="type-toggle">
          <button
            className={`type-btn expense${type === 'expense' ? ' active' : ''}`}
            onClick={() => { setType('expense'); setCategoryId(null); }}
          >
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 19V5M5 12l7 7 7-7"/></svg>
            Dépense
          </button>
          <button
            className={`type-btn income${type === 'income' ? ' active' : ''}`}
            onClick={() => { setType('income'); setCategoryId(null); }}
          >
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 5v14M19 12l-7-7-7 7"/></svg>
            Revenu
          </button>
        </div>

        {/* Category */}
        <div className="field-group">
          <label className="field-label">
            Catégorie
            {filteredCats.length === 0 && <span className="field-opt"> — aucune catégorie pour ce type</span>}
          </label>
          {filteredCats.length > 0 ? (
            <div className="cat-grid">
              {filteredCats.map(cat => (
                <button
                  key={cat._id}
                  className={`cat-grid-btn${categoryId === cat._id ? ' selected' : ''}`}
                  style={{ '--cc': cat.color || '#7C3AED' }}
                  onClick={() => setCategoryId(cat._id)}
                >
                  <span className="cgb-dot" style={{ background: cat.color || '#7C3AED' }} />
                  <span className="cgb-name">{cat.name}</span>
                </button>
              ))}
            </div>
          ) : (
            <div style={{ padding: '10px 0', color: 'var(--text3)', fontSize: 13 }}>
              Créez d'abord des catégories de type « {type === 'expense' ? 'Dépense' : 'Revenu'} » dans le tableau de bord.
            </div>
          )}
        </div>

        {/* Amount + Day */}
        <div className="field-row">
          <div className="field-group">
            <label className="field-label">Montant (€)</label>
            <AmountInput value={amount} onChange={setAmount} disabled={saving} />
          </div>
          <div className="field-group">
            <label className="field-label">Jour du prélèvement</label>
            <select
              className="field-input"
              value={dayOfMonth}
              onChange={e => setDayOfMonth(Number(e.target.value))}
            >
              {DAYS.map(d => (
                <option key={d} value={d}>
                  {d === 1 ? '1er du mois' : `${d} du mois`}
                </option>
              ))}
            </select>
            <span style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
              Prélevé automatiquement le {ordinal(dayOfMonth)} chaque mois
            </span>
          </div>
        </div>
      </div>

      {error && <div className="modal-error">{error}</div>}
      <div className="modal-actions" style={{ justifyContent: sub && onDelete ? 'space-between' : 'flex-end' }}>
        {sub && onDelete && (
          <button
            className="btn-ghost"
            style={{ color: 'var(--red)', borderColor: 'oklch(55% 0.18 25 / 0.3)' }}
            onClick={onDelete}
            disabled={saving}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M3 6h18M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
            </svg>
            Supprimer
          </button>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn-ghost" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving || filteredCats.length === 0}>
            {saving ? 'Enregistrement...' : sub ? 'Enregistrer les modifications' : 'Créer l\'abonnement'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

/* ── Subscription card ── */
const SubscriptionCard = ({ sub, cat, onToggle, onEdit }) => {
  const catColor = cat?.color || (sub.type === 'income' ? '#22C55E' : '#7C3AED');
  return (
    <div className={`sub-card clickable${sub.isActive ? '' : ' inactive'}`} onClick={onEdit}>
      <div className="sub-card-main">
        <div className="sub-card-info">
          <span className="sub-cat-dot" style={{ background: catColor }} />
          <div style={{ minWidth: 0 }}>
            <div className="sub-name">{sub.name}</div>
            <div className="sub-meta">
              {cat && (
                <span className="tx-cat" style={{ '--cat-color': catColor }}>
                  {cat.name}
                </span>
              )}
              <span className="sub-day">le {ordinal(sub.dayOfMonth)} du mois</span>
              <span className={`sub-type-badge ${sub.type}`}>
                {sub.type === 'expense' ? 'Dépense' : 'Revenu'}
              </span>
              {!sub.isActive && <span className="sub-off-badge">Inactif</span>}
            </div>
          </div>
        </div>
        <div className="sub-card-right">
          <span className={`sub-amount ${sub.type === 'income' ? 'green' : 'red'}`}>
            {sub.type === 'income' ? '+' : '−'}{fmt(sub.amount)}/mois
          </span>
          <label className="sub-toggle" title={sub.isActive ? 'Désactiver' : 'Activer'} onClick={e => e.stopPropagation()}>
            <input type="checkbox" checked={sub.isActive} onChange={onToggle} />
            <span className="sub-toggle-slider" />
          </label>
        </div>
      </div>
    </div>
  );
};

/* ── Main page ── */
const SubscriptionPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNewSub, setShowNewSub] = useState(false);
  const [editingSub, setEditingSub] = useState(null);

  const loadAccountData = async (accountId) => {
    const [exp, inc, subsRes] = await Promise.all([
      listCategories({ type: 'expense', accountId }),
      listCategories({ type: 'income', accountId }),
      subscriptionsAPI.list(accountId),
    ]);
    setCategories([...exp, ...inc]);
    setSubscriptions(subsRes?.data?.data || []);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const res = await accountsAPI.list();
        const accs = res?.data?.data || [];
        setAccounts(accs);
        const def = accs.find(a => a.isDefault) || accs[0];
        if (def) {
          setSelectedAccountId(def._id);
          await loadAccountData(def._id);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAccountChange = async (accId) => {
    if (accId === selectedAccountId) return;
    setSelectedAccountId(accId);
    setLoading(true);
    try {
      await loadAccountData(accId);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (sub) => {
    try {
      const res = await subscriptionsAPI.toggle(sub._id);
      setSubscriptions(prev => prev.map(s => s._id === sub._id ? res.data.data : s));
    } catch (e) {
      console.error(e);
    }
  };

  const reloadSubs = async () => {
    const res = await subscriptionsAPI.list(selectedAccountId);
    setSubscriptions(res?.data?.data || []);
  };

  const handleSaveSub = async (data) => {
    await subscriptionsAPI.create(data);
    await reloadSubs();
  };

  const handleEditSub = async (id, data) => {
    await subscriptionsAPI.update(id, data);
    await reloadSubs();
  };

  const account = accounts.find(a => a._id === selectedAccountId);
  const catMap = Object.fromEntries(categories.map(c => [c._id, c]));

  const activeExpense = subscriptions.filter(s => s.isActive && s.type === 'expense').reduce((sum, s) => sum + s.amount, 0);
  const activeIncome = subscriptions.filter(s => s.isActive && s.type === 'income').reduce((sum, s) => sum + s.amount, 0);
  const activeCount = subscriptions.filter(s => s.isActive).length;
  const inactiveCount = subscriptions.filter(s => !s.isActive).length;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Abonnements</h1>
          <p className="page-sub">{account ? account.label : 'Chargement...'}</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={() => setShowNewSub(true)} disabled={!selectedAccountId}>
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
            Nouvel abonnement
          </button>
        </div>
      </div>

      {/* Account selector */}
      {accounts.length > 0 && (
        <div className="acc-selector-grid">
          {accounts.map((a, i) => (
            <div
              key={a._id}
              className={`acc-selector-card${selectedAccountId === a._id ? ' active' : ''}`}
              onClick={() => handleAccountChange(a._id)}
            >
              <div className="account-top">
                <span className="account-dot" style={{ background: ACCOUNT_COLORS[i % ACCOUNT_COLORS.length] }} />
                <span className="account-bank">{a.bank || 'Banque'}</span>
                {a.isDefault && <span className="badge-default">Défaut</span>}
              </div>
              <div className="account-name">{a.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Total banner */}
      <div className="sub-total-banner">
        <div className="sub-total-left">
          <div className="sub-total-label">Total mensuel des abonnements actifs</div>
          <div className="sub-total-amount">
            {activeExpense === 0 && activeIncome === 0 ? (
              <span style={{ color: 'var(--text3)', fontSize: 20 }}>Aucun abonnement actif</span>
            ) : (
              <>
                {activeExpense > 0 && <span className="red">−{fmt(activeExpense)}/mois</span>}
                {activeExpense > 0 && activeIncome > 0 && <span style={{ margin: '0 12px', opacity: 0.4 }}>·</span>}
                {activeIncome > 0 && <span className="green">+{fmt(activeIncome)}/mois</span>}
              </>
            )}
          </div>
        </div>
        <div className="sub-total-right">
          <span className="sub-count-badge active">
            {activeCount} actif{activeCount !== 1 ? 's' : ''}
          </span>
          {inactiveCount > 0 && (
            <span className="sub-count-badge inactive">
              {inactiveCount} inactif{inactiveCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Subscription list */}
      {loading ? (
        <div className="loading-overlay"><div className="loading-spinner" /></div>
      ) : subscriptions.length === 0 ? (
        <div className="tx-empty">
          <svg width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.25" viewBox="0 0 24 24" style={{ opacity: 0.3 }}>
            <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2z"/>
            <path d="M12 6v6l4 2"/>
          </svg>
          <p>Aucun abonnement sur ce compte.</p>
          <button className="btn-primary" onClick={() => setShowNewSub(true)}>
            Créer un abonnement
          </button>
        </div>
      ) : (
        <div className="sub-list">
          {[...subscriptions].sort((a, b) => b.dayOfMonth - a.dayOfMonth).map(sub => (
            <SubscriptionCard
              key={sub._id}
              sub={sub}
              cat={catMap[sub.categoryId]}
              onToggle={() => handleToggle(sub)}
              onEdit={() => setEditingSub(sub)}
            />
          ))}
        </div>
      )}

      {showNewSub && (
        <SubscriptionModal
          title="Nouvel abonnement"
          accountId={selectedAccountId}
          categories={categories}
          onClose={() => setShowNewSub(false)}
          onSave={handleSaveSub}
        />
      )}

      {editingSub && (
        <SubscriptionModal
          title="Modifier l'abonnement"
          sub={editingSub}
          accountId={selectedAccountId}
          categories={categories}
          onClose={() => setEditingSub(null)}
          onSave={handleEditSub}
          onDelete={async () => {
            if (!window.confirm('Supprimer cet abonnement ? Cette action est irréversible.')) return;
            await subscriptionsAPI.remove(editingSub._id);
            setSubscriptions(prev => prev.filter(s => s._id !== editingSub._id));
            setEditingSub(null);
          }}
        />
      )}
    </div>
  );
};

export default SubscriptionPage;
